// sourceViewerModel — the source viewer's pure half (#89): its "Used in" and
// "Facts" lists, numbered, and the passages the centre marks for the list
// being read. One number per card, the same number on its marker, so the
// pair can be selected from either side.
//
// Pure: values in, values out. No React, no copy.

import type { Claim, ClaimPassage } from "../ClaimRow/ClaimRow.js";
import type { MaterialRef } from "../Navigation/types.js";
import type { PdfMarker } from "../SourceView/PdfPages.js";
import type {
    SourceBlock,
    SourceProjection,
    SourceUse,
} from "../SourceView/types.js";

export type ViewerTab = "used" | "facts" | "details";

// Bytes in the reader's locale ("1,2 MB").
export const formatSize = (bytes: number, locale: string): string => {
    const units: readonly string[] = [
        "byte",
        "kilobyte",
        "megabyte",
        "gigabyte",
    ];
    const at = Math.min(
        units.length - 1,
        Math.max(0, Math.floor(Math.log(Math.max(bytes, 1)) / Math.log(1024))),
    );
    return new Intl.NumberFormat(locale, {
        style: "unit",
        unit: units[at],
        unitDisplay: "short",
        maximumFractionDigits: at < 2 ? 0 : 1,
    }).format(bytes / 1024 ** at);
};

// === Used in =================================================================

type NumberedUse = SourceUse & { readonly n: number };

// One material's uses (in reading order), and whether it was made from the
// source as a whole (`derives-from`): a material made from it that no
// statement-level use names still shows, so no use is hidden.
export type UseGroup = {
    readonly material: MaterialRef;
    readonly uses: readonly NumberedUse[];
    readonly madeFrom: boolean;
};

export const useGroupsOf = (
    uses: readonly SourceUse[],
    madeFrom: readonly MaterialRef[],
): readonly UseGroup[] => {
    const numbered = uses.map((u, i): NumberedUse => ({ ...u, n: i + 1 }));
    const materials = [...numbered.map((u) => u.material), ...madeFrom].filter(
        (m, i, all) => all.findIndex((x) => x.id === m.id) === i,
    );
    return materials.map((material) => ({
        material,
        uses: numbered.filter((u) => u.material.id === material.id),
        madeFrom: madeFrom.some((m) => m.id === material.id),
    }));
};

// The "Used in N" count: every use, plus every material made from the
// source that names no use of its own.
export const usedInCount = (groups: readonly UseGroup[]): number =>
    groups.reduce((n, g) => n + (g.uses.length === 0 ? 1 : g.uses.length), 0);

// === Facts ===================================================================

export type NumberedFact = {
    readonly n: number;
    readonly claim: Claim;
    // The claim's passage in THIS source (a claim may rest on several).
    readonly passage: ClaimPassage | undefined;
    // How many statements use it.
    readonly usedIn: number;
};

export type FactFilter = "all" | "used" | "unused" | "review";

export const factsOf = (
    claims: readonly Claim[],
    sourceId: string,
    uses: readonly SourceUse[],
): readonly NumberedFact[] =>
    claims.map((claim, i) => ({
        n: i + 1,
        claim,
        passage: claim.backing.find((p) => p.sourceId === sourceId),
        usedIn: uses.filter((u) => u.claimId === claim.claimId).length,
    }));

// A fact to look at again: its passage is no longer in the source.
const needsReview = (f: NumberedFact): boolean =>
    f.passage === undefined || !f.passage.located;

const keeps = (filter: FactFilter, f: NumberedFact): boolean => {
    switch (filter) {
        case "all":
            return true;
        case "used":
            return f.usedIn > 0;
        case "unused":
            return f.usedIn === 0;
        case "review":
            return needsReview(f);
    }
};

export const filterFacts = (
    facts: readonly NumberedFact[],
    filter: FactFilter,
): readonly NumberedFact[] => facts.filter((f) => keeps(filter, f));

export const reviewCount = (facts: readonly NumberedFact[]): number =>
    facts.filter(needsReview).length;

// === What the centre marks ===================================================

// A referenced passage the centre marks: its number, words and page (absent
// when the source has no pages or none was recorded).
export type Reference = {
    readonly n: number;
    readonly quote: string;
    readonly page?: number;
};

// The passages of the list being read: the uses (also on Details), or the
// facts whose passage is still in the source.
export const referencesOf = (
    tab: ViewerTab,
    groups: readonly UseGroup[],
    facts: readonly NumberedFact[],
): readonly Reference[] => {
    switch (tab) {
        case "used":
        case "details":
            return groups.flatMap((g) =>
                g.uses.map(
                    (u): Reference => ({
                        n: u.n,
                        quote: u.quote,
                        ...(u.page === undefined ? {} : { page: u.page }),
                    }),
                ),
            );
        case "facts":
            return facts.flatMap((f): Reference[] =>
                f.passage === undefined || !f.passage.located
                    ? []
                    : [
                          {
                              n: f.n,
                              quote: f.passage.exact,
                              ...(f.passage.page === undefined
                                  ? {}
                                  : { page: f.passage.page }),
                          },
                      ],
            );
    }
};

// The PDF markers: references that know their page.
export const pdfMarkersOf = (
    references: readonly Reference[],
): readonly PdfMarker[] =>
    references.flatMap((r): PdfMarker[] =>
        r.page === undefined ? [] : [{ n: r.n, quote: r.quote, page: r.page }],
    );

// The read text with every marked passage's block highlighted (the text
// viewer, and the default viewer's read text). Quotes match loosely, as the
// rest of the app matches them. Kinds without text blocks pass through.
const loose = (text: string): string =>
    text.replace(/\s+/g, " ").trim().toLocaleLowerCase();

// The paragraphs of a painted document (a Word page render) that hold a
// referenced passage: paragraph index → the numbers it carries, in order.
// A reference marks only the first paragraph that holds it.
export const paragraphMarksOf = (
    paragraphs: readonly string[],
    references: readonly Reference[],
): ReadonlyMap<number, readonly number[]> => {
    const texts = paragraphs.map(loose);
    const hits = references.flatMap((r): { i: number; n: number }[] => {
        const q = loose(r.quote);
        const i = q === "" ? -1 : texts.findIndex((t) => t.includes(q));
        return i < 0 ? [] : [{ i, n: r.n }];
    });
    return new Map(
        [...new Set(hits.map((h) => h.i))].map((i) => [
            i,
            hits
                .filter((h) => h.i === i)
                .map((h) => h.n)
                .sort((a, b) => a - b),
        ]),
    );
};

// A block holding referenced passages is highlighted and carries the number
// of every reference it holds, in order (its margin markers).
const markBlocks = (
    blocks: readonly SourceBlock[],
    references: readonly Reference[],
): readonly SourceBlock[] => {
    const quotes = references.map((r) => ({ n: r.n, quote: loose(r.quote) }));
    return blocks.map((block): SourceBlock => {
        const text = loose(block.text);
        const markers = quotes
            .filter((q) => q.quote !== "" && text.includes(q.quote))
            .map((q) => q.n)
            .sort((a, b) => a - b);
        return markers.length === 0
            ? block
            : { type: "highlighted", id: block.id, text: block.text, markers };
    });
};

export const markPassages = (
    content: SourceProjection,
    references: readonly Reference[],
): SourceProjection => {
    switch (content.type) {
        case "text":
        case "pdf":
        case "word":
            return {
                ...content,
                blocks: markBlocks(content.blocks, references),
            };
        case "video":
        case "image":
        case "media":
        case "unavailable":
            return content;
    }
};
